// src/Pages/finanzas-clientes/useAseosSection.ts
//
// Round 2026-06-05 — la pestaña "Aseos" de /finanzas-cliente muestra los
// aseos REALES creados en el calendario (tabla `aseos` / modelo Cleaning),
// NO flujos manuales con categoría=aseo (esos viven en "Otros"). Este hook
// consume el endpoint dedicado `GET /finanzas/aseos/:id`. Ver
// docs/flujo-financiero.md §10.5.1.
import { useCallback, useEffect, useState } from "react";
import { getMsg } from "../../utils/antdStatic";
import { Dayjs } from "dayjs";
import { apiFetch } from "../../services/apiClient";

// Fila de aseo de calendario tal como la devuelve el backend.
export type AseoRow = {
  id: number;
  fecha_inicio: string | null;
  fecha_termino: string | null;
  hora_aseo: string | null;
  urgencia: string | null;
  estado: string | null;
  categoria: string | null;
  costo_limpieza: number;
  // Quién paga el aseo: { tipo: 0=AF, 1=Propietario, 2=Arrendatario, ... }.
  pagado_por: { tipo: number; nombre?: string | null } | null;
  aseador: string | null;
  observaciones: string | null;
  reserva_id: number | null;
  propiedad_id: number | null;
  // Efecto en el bolsillo del cliente: negativo si él paga (pagado_por.tipo=1),
  // 0 si lo paga AF o el arrendatario.
  efectoCliente: number;
};

// Subtotal del período: el aseo solo puede ser egreso para el cliente (nunca
// ingreso), así que reutilizamos la forma `{egresoCliente}` que ya entiende
// `renderClienteSignedSummary` (ingreso=0 → colapsa al "Subtotal" simple).
export type AseoTotals = {
  egresoCliente?: number;
  currency?: string;
};

export function useAseosSection(
  clientId: number | null | undefined,
  selectedPropId: number | null,
) {
  const [rows, setRows] = useState<AseoRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState<AseoTotals | null>(null);
  const [month, setMonth] = useState<Dayjs | null>(null);

  const fetchRows = useCallback(
    async (p = page, s = size) => {
      if (!clientId || !selectedPropId) return;
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(p));
        params.set("size", String(s));
        params.set("propId", String(selectedPropId));
        if (month) {
          params.set("from", month.startOf("month").toISOString());
          params.set("to", month.endOf("month").toISOString());
        }

        const json = await apiFetch<any>(
          `/finanzas/aseos/${clientId}?${params.toString()}`,
        );
        if (!json) throw new Error("No se pudieron obtener los aseos");

        setRows(json.data || []);
        setTotal(json.total || 0);
        setPage(json.page || 1);
        setSize(json.size || 20);
        setTotals(json.totals || null);
      } catch (e: any) {
        getMsg()?.error(e?.message || "Error cargando aseos");
      } finally {
        setLoading(false);
      }
    },
    [clientId, selectedPropId, page, size, month],
  );

  useEffect(() => {
    if (clientId && selectedPropId) fetchRows(1, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, selectedPropId, month]);

  return {
    rows,
    total,
    page,
    size,
    loading,
    totals,
    month,
    setMonth,
    onPaginate: (p: number, s?: number) => fetchRows(p, s ?? size),
    refetch: fetchRows,
  };
}
